import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Server, ShieldCheck, ShieldAlert, Search, Loader2 } from 'lucide-react';

const SuperAdminSecurity = () => {
    const [auditLogs, setAuditLogs] = useState([]);
    const [logsLoading, setLogsLoading] = useState(true);
    const [systemSettings, setSystemSettings] = useState({ maintenance_mode: false });
    const [settingsLoading, setSettingsLoading] = useState(true);
    const [logFilter, setLogFilter] = useState('');
    const [serverStats, setServerStats] = useState({
        cpu: 0,
        memory: 0,
        disk: 0,
        dbStatus: 'offline',
        s3Status: 'offline',
        pm2Status: 'offline'
    });
    const [statsLoading, setStatsLoading] = useState(true);

    const headers = {
        Authorization: `Bearer ${localStorage.getItem('token')}`
    };

    const fetchAuditLogs = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get('http://localhost:4000/api/audit-logs', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setAuditLogs(response.data);
        } catch (err) {
            console.error("Error loading audit logs:", err);
        } finally {
            setLogsLoading(false);
        }
    };

    const fetchSystemSettings = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get('http://localhost:4000/api/superadmin/settings', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setSystemSettings(response.data);
        } catch (err) {
            console.error("Error loading system settings:", err);
        } finally {
            setSettingsLoading(false);
        }
    };

    const fetchServerStats = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get('http://localhost:4000/api/superadmin/stats', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setServerStats(response.data);
        } catch (err) {
            console.error("Error loading server stats:", err);
        } finally {
            setStatsLoading(false);
        }
    };

    useEffect(() => {
        fetchAuditLogs();
        fetchSystemSettings();
        fetchServerStats();

        // Polling de métricas en tiempo real cada 5 segundos
        const interval = setInterval(fetchServerStats, 5000);
        return () => clearInterval(interval);
    }, []);

    const handleToggleMaintenance = async () => {
        const nextVal = !systemSettings.maintenance_mode;
        try {
            const token = localStorage.getItem('token');
            await axios.post('http://localhost:4000/api/superadmin/settings', {
                key: 'maintenance_mode',
                value: nextVal
            }, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setSystemSettings(prev => ({ ...prev, maintenance_mode: nextVal }));
        } catch (err) {
            console.error("Error saving maintenance mode setting:", err);
            alert("No se pudo actualizar el modo mantenimiento.");
        }
    };

    return (
        <div className="min-h-screen bg-theme-bg text-theme-text-primary p-4 md:p-8 font-sans pb-20">
            <div className="max-w-7xl mx-auto space-y-6">
                
                {/* Header */}
                <div className="border-b border-theme-border/80 pb-6">
                    <h1 className="text-3xl font-extrabold tracking-tight text-theme-text-primary flex items-center gap-3">
                        <ShieldAlert className="text-purple-500" size={32} /> Seguridad e Infraestructura
                    </h1>
                    <p className="text-theme-text-secondary text-xs mt-1">Monitoreo de recursos de hardware en vivo, control del Modo Mantenimiento y logs de auditoría de seguridad.</p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Tarjeta 1: Métricas reales del OS */}
                    <div className="bg-theme-panel/60 backdrop-blur-xl border border-theme-border rounded-3xl p-6 shadow-xl space-y-4">
                        <h3 className="text-base font-bold text-theme-text-primary flex items-center gap-2">
                            <Server size={18} className="text-purple-450" /> Métricas del Servidor (Real)
                        </h3>
                        {statsLoading ? (
                            <div className="flex justify-center py-10">
                                <Loader2 className="animate-spin text-purple-500" size={24} />
                            </div>
                        ) : (
                            <div className="space-y-3">
                                <div>
                                    <div className="flex justify-between text-xs text-theme-text-secondary mb-1">
                                        <span>Uso de CPU</span>
                                        <span className="font-bold text-theme-text-primary">{serverStats.cpu}%</span>
                                    </div>
                                    <div className="w-full bg-theme-bg h-2 rounded-full overflow-hidden">
                                        <div className="bg-purple-500 h-full rounded-full transition-all duration-500" style={{ width: `${serverStats.cpu}%` }}></div>
                                    </div>
                                </div>
                                <div>
                                    <div className="flex justify-between text-xs text-theme-text-secondary mb-1">
                                        <span>Uso de Memoria</span>
                                        <span className="font-bold text-theme-text-primary">{serverStats.memory}%</span>
                                    </div>
                                    <div className="w-full bg-theme-bg h-2 rounded-full overflow-hidden">
                                        <div className="bg-blue-500 h-full rounded-full transition-all duration-500" style={{ width: `${serverStats.memory}%` }}></div>
                                    </div>
                                </div>
                                <div>
                                    <div className="flex justify-between text-xs text-theme-text-secondary mb-1">
                                        <span>Espacio en Disco</span>
                                        <span className="font-bold text-theme-text-primary">{serverStats.disk}%</span>
                                    </div>
                                    <div className="w-full bg-theme-bg h-2 rounded-full overflow-hidden">
                                        <div className="bg-indigo-500 h-full rounded-full transition-all duration-500" style={{ width: `${serverStats.disk}%` }}></div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Tarjeta 2: Conexiones */}
                    <div className="bg-theme-panel/60 backdrop-blur-xl border border-theme-border rounded-3xl p-6 shadow-xl space-y-4">
                        <h3 className="text-base font-bold text-theme-text-primary flex items-center gap-2">
                            <ShieldCheck size={18} className="text-purple-450" /> Estado de Conexiones
                        </h3>
                        <div className="space-y-3">
                            <div className="flex justify-between items-center p-2.5 bg-theme-bg/30 border border-theme-border/50 rounded-2xl">
                                <span className="text-xs text-theme-text-secondary">Base de Datos PostgreSQL</span>
                                <span className="bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-[10px] px-2 py-0.5 rounded-full font-bold">ACTIVO</span>
                            </div>
                            <div className="flex justify-between items-center p-2.5 bg-theme-bg/30 border border-theme-border/50 rounded-2xl">
                                <span className="text-xs text-theme-text-secondary">Almacenamiento S3 / MinIO</span>
                                <span className="bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-[10px] px-2 py-0.5 rounded-full font-bold">ACTIVO</span>
                            </div>
                            <div className="flex justify-between items-center p-2.5 bg-theme-bg/30 border border-theme-border/50 rounded-2xl">
                                <span className="text-xs text-theme-text-secondary">Administrador de Procesos PM2</span>
                                <span className="bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-[10px] px-2 py-0.5 rounded-full font-bold">ONLINE</span>
                            </div>
                        </div>
                    </div>

                    {/* Tarjeta 3: Control de Mantenimiento */}
                    <div className="bg-theme-panel/60 backdrop-blur-xl border border-theme-border rounded-3xl p-6 shadow-xl space-y-4">
                        <h3 className="text-base font-bold text-theme-text-primary flex items-center gap-2">
                            <ShieldAlert size={18} className="text-purple-450" /> Control de Plataforma
                        </h3>
                        <p className="text-xs text-theme-text-secondary leading-relaxed">
                            Activa el Modo Mantenimiento para bloquear temporalmente el acceso a la plataforma para todos los clientes e ingenieros de obra.
                        </p>
                        <div className="pt-2">
                            {settingsLoading ? (
                                <div className="flex justify-center">
                                    <Loader2 className="animate-spin text-purple-500" size={20} />
                                </div>
                            ) : (
                                <div className="flex items-center justify-between p-3 bg-theme-bg/30 border border-theme-border/50 rounded-2xl">
                                    <span className="text-xs font-bold text-theme-text-primary">Modo Mantenimiento</span>
                                    <button
                                        onClick={handleToggleMaintenance}
                                        className={`px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                                            systemSettings.maintenance_mode
                                                ? 'bg-rose-600 text-white shadow-lg shadow-rose-950/20'
                                                : 'bg-theme-panel text-theme-text-secondary hover:text-theme-text-primary border border-theme-border'
                                        }`}
                                    >
                                        {systemSettings.maintenance_mode ? 'ACTIVADO (Bloquear API)' : 'DESACTIVADO (Permitir)'}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Historial de Logs de Auditoría */}
                <div className="bg-theme-panel/60 backdrop-blur-xl border border-theme-border rounded-3xl p-6 shadow-xl">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
                        <div>
                            <h3 className="text-lg font-bold text-theme-text-primary">Registro de Auditoría de Seguridad</h3>
                            <p className="text-xs text-theme-text-secondary mt-1">Últimas 100 operaciones de infraestructura y acciones de usuarios.</p>
                        </div>
                        <div className="relative w-full sm:w-64">
                            <input
                                type="text"
                                placeholder="Buscar logs..."
                                value={logFilter}
                                onChange={(e) => setLogFilter(e.target.value)}
                                className="w-full bg-theme-bg border border-theme-border text-theme-text-primary text-xs rounded-xl pl-9 pr-4 py-2 focus:outline-none focus:border-purple-500"
                            />
                            <Search size={14} className="absolute left-3 top-2.5 text-zinc-500" />
                        </div>
                    </div>

                    {logsLoading ? (
                        <div className="flex flex-col items-center justify-center py-12 text-zinc-550 gap-3">
                            <Loader2 className="animate-spin text-purple-500" size={32} />
                            <span className="text-xs">Cargando logs de seguridad...</span>
                        </div>
                    ) : (
                        <div className="overflow-x-auto rounded-2xl border border-theme-border">
                            <table className="w-full text-left border-collapse text-xs">
                                <thead>
                                    <tr className="bg-theme-bg border-b border-theme-border text-theme-text-secondary font-bold uppercase tracking-wider text-[10px]">
                                        <th className="p-4">Timestamp</th>
                                        <th className="p-4">Usuario</th>
                                        <th className="p-4">Acción</th>
                                        <th className="p-4">Entidad</th>
                                        <th className="p-4">Obra / Proyecto</th>
                                        <th className="p-4">IP Address</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-theme-border/30 text-theme-text-primary">
                                    {auditLogs
                                        .filter(log => {
                                            const matchText = `${log.action} ${log.entity_type} ${log.user_name} ${log.user_email} ${log.project_name} ${log.ip_address}`.toLowerCase();
                                            return matchText.includes(logFilter.toLowerCase());
                                        })
                                        .map(log => (
                                            <tr key={log.id} className="hover:bg-theme-border/10 transition-colors">
                                                <td className="p-4 font-mono text-[10px] text-theme-text-secondary">{new Date(log.created_at).toLocaleString()}</td>
                                                <td className="p-4">
                                                    <div className="font-bold">{log.user_name || 'Sistema'}</div>
                                                    <div className="text-[10px] text-theme-text-secondary font-mono">{log.user_email || 'cronjob/system'}</div>
                                                </td>
                                                <td className="p-4 font-bold text-purple-400">{log.action}</td>
                                                <td className="p-4">{log.entity_type} <span className="text-[10px] text-theme-text-secondary font-mono">#{log.entity_id}</span></td>
                                                <td className="p-4 font-medium">{log.project_name || '-'}</td>
                                                <td className="p-4 font-mono text-[10px] text-theme-text-secondary">{log.ip_address || '-'}</td>
                                            </tr>
                                        ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SuperAdminSecurity;
